import { FeaturedIcon } from '@backrs_ui/common-ui';

import { GuideCopyAction } from '@backrs_ui/grow-ui/src/components/GuidePage';
import {
  CollegeVisitCompanionId,
  collegeVisitCompanionContent,
  CollegeVisitQuestion,
} from '@backrs_ui/grow-ui/src/pages/CollegeVisitGuide/content';
import { type CustomTabId } from '@backrs_ui/grow-ui/src/redux/tabs/customTabIds';

import { Box, Divider, SxProps, Theme, Typography } from '@mui/material';

import { useAgentContext } from './CollegeVisitCompanionPanel.agentContext';

export const COLLEGE_VISIT_COMPANION_TAB_IDS: Readonly<
  Record<CollegeVisitCompanionId, CustomTabId>
> = {
  'student-questions': 'collegeVisitStudentQuestions',
  'parent-questions': 'collegeVisitParentQuestions',
  'discussion-guide': 'collegeVisitDiscussionGuide',
};

type CollegeVisitCompanionPanelProps = Readonly<{
  companionId: CollegeVisitCompanionId;
}>;

/** Companion page (student/parent questions, discussion guide) for the College Visit Guide. */
export const CollegeVisitCompanionPanel = ({ companionId }: CollegeVisitCompanionPanelProps) => {
  useAgentContext(companionId);
  const page = collegeVisitCompanionContent[companionId];

  return (
    <Box id={`college-visit-companion-${page.id}`} sx={pageSx}>
      <Box sx={headerSx}>
        <Typography variant="display-xs-semibold" sx={titleSx}>
          {page.title}
        </Typography>
        <Typography variant="text-md-regular" sx={subtitleSx}>
          {page.subtitle}
        </Typography>
      </Box>
      {page.howToUse && (
        <Box sx={howToUseSx}>
          <Typography variant="text-sm-regular" sx={howToUseTextSx}>
            {page.howToUseLabel && (
              <Box component="span" sx={howToUseLabelSx}>
                {page.howToUseLabel}:{' '}
              </Box>
            )}
            {page.howToUse}
          </Typography>
        </Box>
      )}
      {page.groups.map((group) => (
        <Box key={group.id} id={`college-visit-companion-${page.id}-${group.id}`} sx={groupSx}>
          <Box sx={groupHeaderSx}>
            <FeaturedIcon
              iconName={group.icon}
              size="sm"
              shape="square"
              color="Colors/Text/text-secondary"
            />
            <Box sx={groupHeadingSx}>
              <Typography variant="text-lg-semibold" sx={titleSx}>
                {group.title}
              </Typography>
              {group.intro && (
                <Typography variant="text-sm-regular" sx={subtitleSx}>
                  {group.intro}
                </Typography>
              )}
            </Box>
            <GuideCopyAction
              id={`college-visit-companion-${page.id}-${group.id}-copy`}
              text={formatGroupForCopy(group.title, group.questions)}
            />
          </Box>
          <Box sx={listSx}>
            {group.questions.map((item, index) => (
              <Box key={item.question} sx={questionSx}>
                {index > 0 && <Divider sx={dividerSx} />}
                <Box sx={questionBodySx}>
                  <Typography variant="text-sm-semibold" sx={numberSx}>
                    {index + 1}
                  </Typography>
                  <Box sx={questionTextSx}>
                    <Typography variant="text-sm-medium" sx={titleSx}>
                      {item.question}
                    </Typography>
                    {item.note && (
                      <Typography variant="text-sm-regular" sx={subtitleSx}>
                        {item.note}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const formatGroupForCopy = (title: string, questions: readonly CollegeVisitQuestion[]) =>
  [title, ...questions.map((item, index) => `${index + 1}. ${item.question}`)].join('\n');

export const CollegeVisitStudentQuestionsTab = () => (
  <CollegeVisitCompanionPanel companionId="student-questions" />
);

export const CollegeVisitParentQuestionsTab = () => (
  <CollegeVisitCompanionPanel companionId="parent-questions" />
);

export const CollegeVisitDiscussionGuideTab = () => (
  <CollegeVisitCompanionPanel companionId="discussion-guide" />
);

const PAGE_MAX_WIDTH = 800;

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
  maxWidth: PAGE_MAX_WIDTH,
  mx: 'auto',
  py: backrs.spacing['spacing-4xl'],
  px: backrs.spacing['spacing-3xl'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const howToUseSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const howToUseTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const howToUseLabelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const groupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  p: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const groupHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

const groupHeadingSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  minWidth: 0,
  flex: 1,
});

const listSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const questionSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-secondary'],
  my: backrs.spacing['spacing-lg'],
});

const questionBodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

const numberSx: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  width: 32,
  textAlign: 'center',
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const questionTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  minWidth: 0,
});
